/**
 * PRIV-01 (#138): the theme's consent gate.
 *
 * The theme writes exactly two third-party script tags of its own - Google Tag Manager and the
 * Zendesk widget. Everything else on the page (Klaviyo, Lucky Orange, Judge.me, Helium,
 * Simprosys, every web pixel) arrives inside `{{ content_for_header }}` and cannot be wrapped from
 * a theme. This module owns the GTM half: it boots the container only once Shopify's Customer
 * Privacy API says analytics processing is allowed, re-checks on every `visitorConsentCollected`,
 * and honours a Global Privacy Control signal by recording a denial before anything loads.
 *
 * WHY THIS IS SAFE TO SHIP BEFORE THE BANNER IS ON. Shopify's documented default is that "for
 * regions that are configured to require consent, non-essential purposes are not allowed by
 * default until consent is given. For other regions, the default behavior is to allow all
 * processing purposes" (https://shopify.dev/docs/api/customer-privacy). With no consent region
 * configured, `analyticsProcessingAllowed()` is true for everyone and GTM boots exactly as it did
 * before. The gate only starts withholding anything once the owner enables the cookie banner in
 * Settings > Customer privacy.
 *
 * ORDER. `window.Shopify.loadFeatures` is defined by `{{ content_for_header }}`, so this module
 * must run AFTER it. A `<script type="module">` is deferred by definition, which is exactly what we
 * need: the old IIFE's position as the first thing in <head> bought nothing once the boot is
 * asynchronous anyway, because `gtm.js` was always injected `async`.
 *
 * NO GLOBALS AND NO BARE TIMERS. Everything is reached through an injected `scope` - the window in
 * the browser, a plain object in the tests. That is also what keeps the module inside the ESLint
 * globals allowlist for `assets/woopf-*.js`, which grants `window`, `document` and `navigator` but
 * not `setTimeout` or `sessionStorage`.
 *
 * Unit tests: `tests/privacy/consent.test.ts`.
 */

/** The container this theme has always loaded (layout/theme.liquid:15 before PRIV-01). */
export const GTM_CONTAINER_ID = 'GTM-WMCG892L';

/**
 * How long we wait for Shopify's consent-tracking API before treating it as unavailable.
 * `loadFeatures` is a queue stub at parse time and its real implementation is fetched from
 * Shopify's CDN, so the callback can be several hundred milliseconds late on a slow connection.
 */
export const CONSENT_API_TIMEOUT_MS = 4000;

/** Written on <html> so QA and the owner can see which branch ran, without a console line. */
export const CONSENT_STATE_ATTRIBUTE = 'data-wf-consent';

/** Set once per session so a GPC visitor is not re-denied on every page view. */
export const GPC_SESSION_KEY = 'wf-gpc-applied';

export const PRIVACY_CHOICES_SELECTOR = '[data-wf-privacy-choices]';
export const PRIVACY_CHOICES_LABEL_SELECTOR = '[data-wf-privacy-choices-label]';
export const SALE_OF_DATA_LABEL = 'Your Privacy Choices';
export const DEFAULT_PRIVACY_LABEL = 'Cookie settings';

/*
 * I18N-06 (#163): visible strings come from the page; the English literal is the fallback.
 * `snippets/c-privacy-choices.liquid` and its WOOPF footer twin `snippets/woopf-privacy-choices.liquid`
 * print the `privacy-choices` bridge beside the footer link
 * this module relabels, and a module runs deferred, after the footer has been parsed. Module scope
 * is not the global scope, so these three names are this module's own. Vitest has no `document`:
 * the reader then returns an empty object and every label is its English fallback.
 */
const wfStrings = (() => {
  try {
    const node = document.querySelector(
      'script[type="application/json"][data-wf-i18n="privacy-choices"]',
    );
    const parsed = node ? JSON.parse(node.textContent) : null;
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
})();
// Shopify's `t` HTML-escapes every translation whose key does not end in `_html` (French
// "n'a" arrives as "n&#39;a"), so the page's values are decoded back to plain text.
const wfDecode = (text) =>
  text.replace(/&(#x[0-9a-f]+|#\d+|amp|lt|gt|quot|apos);/gi, (entity, body) => {
    const named = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'" }[body.toLowerCase()];
    if (named) return named;
    const code =
      body[1] === 'x' || body[1] === 'X'
        ? parseInt(body.slice(2), 16)
        : parseInt(body.slice(1), 10);
    return code > 0 && code <= 0x10ffff ? String.fromCodePoint(code) : entity;
  });
const tr = (key, fallback, values = {}) => {
  const text =
    typeof wfStrings[key] === 'string' && wfStrings[key] !== ''
      ? wfDecode(wfStrings[key])
      : fallback;
  return Object.keys(values).reduce((out, name) => out.split(`{${name}}`).join(values[name]), text);
};

/**
 * Resolve `window.Shopify.customerPrivacy`, or null when it cannot be reached.
 *
 * @param {Window} scope
 * @returns {Promise<object|null>}
 */
export const loadCustomerPrivacy = (scope) =>
  new Promise((resolve) => {
    const shopify = scope && scope.Shopify;
    if (!shopify || typeof shopify.loadFeatures !== 'function') {
      resolve(null);
      return;
    }

    let settled = false;
    const finish = (value) => {
      if (settled) return;
      settled = true;
      resolve(value);
    };

    if (typeof scope.setTimeout === 'function') {
      scope.setTimeout(() => finish(null), CONSENT_API_TIMEOUT_MS);
    }

    shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
      if (error) {
        finish(null);
        return;
      }
      finish((scope.Shopify && scope.Shopify.customerPrivacy) || null);
    });
  });

/**
 * @param {object|null} api
 * @returns {boolean}
 */
export const analyticsAllowed = (api) =>
  Boolean(
    api && typeof api.analyticsProcessingAllowed === 'function' && api.analyticsProcessingAllowed(),
  );

/**
 * Inject `gtm.js`. Idempotent by reading the document rather than by a module flag, so a Theme
 * Editor reload or a second call cannot produce two containers.
 *
 * @param {Window} scope
 * @param {string} containerId
 * @returns {boolean} true when this call injected the script
 */
export const bootGtm = (scope, containerId = GTM_CONTAINER_ID) => {
  const doc = scope && scope.document;
  if (!doc || typeof doc.createElement !== 'function') return false;

  const source = `https://www.googletagmanager.com/gtm.js?id=${containerId}`;
  if (typeof doc.querySelector === 'function' && doc.querySelector(`script[src="${source}"]`)) {
    return false;
  }

  const layer = scope.dataLayer || [];
  scope.dataLayer = layer;
  layer.push({ 'gtm.start': Date.now(), event: 'gtm.js' });

  const script = doc.createElement('script');
  script.async = true;
  script.src = source;
  if (doc.head && typeof doc.head.appendChild === 'function') doc.head.appendChild(script);
  return true;
};

/**
 * PRIV-03 (#140). True when the visitor's browser is sending Global Privacy Control.
 *
 * Strict `=== true`: some extensions set the string "true".
 *
 * @param {Window} scope
 * @returns {boolean}
 */
export const gpcRequested = (scope) =>
  Boolean(scope && scope.navigator && scope.navigator.globalPrivacyControl === true);

/**
 * PRIV-03 (#140). Record the GPC opt-out once per session.
 *
 * This only ever records a DENIAL. Shopify's documentation warns that consent must never be
 * recorded automatically on a visitor's behalf; a GPC header IS the visitor's own expressed
 * choice, and nothing here ever sets a purpose to true.
 *
 * `preferences` is deliberately left alone: it covers country and language memory, which is not
 * what GPC asks about, and denying it would reset the shopper's market on every page.
 *
 * @param {Window} scope
 * @param {object|null} api
 * @param {() => void} [onApplied]
 * @returns {boolean} true when this call sent the opt-out
 */
export const applyGpc = (scope, api, onApplied) => {
  if (!gpcRequested(scope)) return false;
  if (!api || typeof api.setTrackingConsent !== 'function') return false;

  let store = null;
  try {
    const candidate = scope.sessionStorage;
    if (candidate && candidate.getItem(GPC_SESSION_KEY) === '1') return false;
    store = candidate || null;
  } catch {
    // Private mode and blocked storage both throw on access, so `store` stays null and the
    // denial is simply re-sent on the next page view. Re-sending the same denial is harmless;
    // the assignment is omitted because ESLint's no-useless-assignment rejects a rewrite here.
  }

  api.setTrackingConsent({ analytics: false, marketing: false, sale_of_data: false }, () => {
    if (typeof onApplied === 'function') onApplied();
  });

  try {
    if (store) store.setItem(GPC_SESSION_KEY, '1');
  } catch {
    // Same as above: a blocked store costs one repeated denial, never a missed one.
  }

  return true;
};

/**
 * @param {Document} doc
 * @param {'allowed'|'denied'|'unavailable'} state
 * @returns {boolean}
 */
export const markConsentState = (doc, state) => {
  const root = doc && doc.documentElement;
  if (!root || typeof root.setAttribute !== 'function') return false;
  root.setAttribute(CONSENT_STATE_ATTRIBUTE, state);
  return true;
};

/**
 * PRIV-09 (#145). "Your Privacy Choices" only where a sale-of-data opt-out is required; plain
 * "Cookie settings" everywhere else. I18N-06 (#163): "Cookie settings" is the footer's translated
 * label, read from the bridge, with DEFAULT_PRIVACY_LABEL's text as its fallback. "Your Privacy
 * Choices" is the CPRA's own link text and stays English.
 *
 * @param {object|null} api
 * @returns {string}
 */
export const privacyChoicesLabel = (api) =>
  api && typeof api.saleOfDataRegion === 'function' && api.saleOfDataRegion()
    ? SALE_OF_DATA_LABEL
    : tr('cookie_settings', 'Cookie settings'); // I18N-06 (#163): translated, from the bridge.

/**
 * PRIV-09 (#145). Relabel the footer link and, when Shopify's banner is on the page, make it open
 * the preferences modal instead of navigating. With the banner off, `window.privacyBanner` does
 * not exist and the link keeps its `href` to the privacy policy, which is the whole point of
 * rendering it as an anchor.
 *
 * @param {Document} doc
 * @param {Window} scope
 * @param {object|null} api
 * @returns {number} how many links were bound to the modal
 */
export const initPrivacyChoices = (doc, scope, api) => {
  if (!doc || typeof doc.querySelectorAll !== 'function') return 0;
  const nodes = doc.querySelectorAll(PRIVACY_CHOICES_SELECTOR);
  const label = privacyChoicesLabel(api);
  const banner = scope && scope.privacyBanner;
  const canOpen = Boolean(banner && typeof banner.showPreferences === 'function');
  let bound = 0;

  for (const node of nodes) {
    const text =
      typeof node.querySelector === 'function'
        ? node.querySelector(PRIVACY_CHOICES_LABEL_SELECTOR)
        : null;
    if (text) text.textContent = label;
    if (!canOpen || typeof node.addEventListener !== 'function') continue;
    node.addEventListener('click', (event) => {
      if (event && typeof event.preventDefault === 'function') event.preventDefault();
      banner.showPreferences();
    });
    bound += 1;
  }

  return bound;
};

/**
 * Wire the page. Exported so a test can drive it with a fake window.
 *
 * @param {Window} scope
 * @returns {Promise<'allowed'|'denied'|'unavailable'>}
 */
export const start = async (scope) => {
  const doc = scope && scope.document;
  const api = await loadCustomerPrivacy(scope);

  if (!api) {
    /*
     * Fail closed. If Shopify's consent feature never arrives we do not know what the visitor
     * has agreed to, so the container stays off and the attribute says why. The alternative -
     * booting GTM on failure - is a one-word change here and is the owner's decision to make.
     */
    markConsentState(doc, 'unavailable');
    initPrivacyChoices(doc, scope, null);
    return 'unavailable';
  }

  const settle = () => {
    const allowed = analyticsAllowed(api);
    markConsentState(doc, allowed ? 'allowed' : 'denied');
    if (allowed) bootGtm(scope);
    return allowed;
  };

  const gpcApplied = applyGpc(scope, api, settle);
  initPrivacyChoices(doc, scope, api);

  // A GPC visitor is settled by the `setTrackingConsent` callback above, not here: reading
  // `analyticsProcessingAllowed()` before Shopify has stored the denial would boot GTM for one
  // page view, which is the exact failure this module exists to prevent.
  let allowed = false;
  if (gpcApplied) markConsentState(doc, 'denied');
  else allowed = settle();

  if (doc && typeof doc.addEventListener === 'function') {
    doc.addEventListener('visitorConsentCollected', settle);
  }

  return allowed ? 'allowed' : 'denied';
};

/*
 * The auto-start. `typeof document` guards the Vitest run, which has no DOM at all.
 */
if (typeof document !== 'undefined' && typeof window !== 'undefined') {
  start(window);
}
